import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { parseInt } from 'lodash';
import { useTranslation } from 'react-i18next';

import Button_T from 'common/components/atoms/Button';
import Label_T from 'common/typography/Label/Label';
import FlexContainer from 'common/components/atoms/FlexContainer';
import SpeedInputField from '../atoms/SpeedInputField';
import FilterInlineModal from '../atoms/FilterInlineModal';

const Button = styled(Button_T)`
  margin-left: 55px;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  padding: 0.5em !important;
  ${props => props.maxWidth && 'margin-left: 0;'}
`;

const Label = styled(Label_T)`
  ${props => props.header && `
    color: #3C3D3A;
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 0;
  `}
  ${props => props.error && `
    color: red;
  `}
`;

const SpeedFilter = (props) => {
  const { t } = useTranslation();
  const [filterMin, setFilterMin] = useState(props.filterMin);
  const [filterMax, setFilterMax] = useState(props.filterMax);
  const [filterError, setFilterError] = useState('');
  const buttonRef = useRef(null);

  const isSaveButtonDisabled = useMemo(() => {
    const minFilter = parseInt(filterMin);
    const maxFilter = parseInt(filterMax);

    // speed values cannot be 0 and should be number
    if (!(!!minFilter || !!maxFilter) || minFilter <= 0 || maxFilter <= 0) return true;
    if (filterError) return true;
    if (filterMin === props.filterMin && filterMax === props.filterMax) return true;

    return false;
  }, [filterMin, filterMax, filterError, props.filterMin, props.filterMax]);

  useEffect(() => {
    if (Number(filterMin) > Number(filterMax) && filterMax !== '') {
      if (!filterError) {
        setFilterError('Min value cannot be greater than Max value');
      }
    } else if (Number(filterMin) < 0) {
      if (!filterError) {
        setFilterError('Min value cannot be lower than 0');
      }
    } else if (filterError) setFilterError('');
  }, [filterMin, filterMax]);

  const clearFilterValues = (e) => {
    e.stopPropagation();
    props.onSave('', '');
  };

  const saveFilterValue = (e) => {
    e.stopPropagation();
    props.onSave(Number(filterMin), Number(filterMax));
  };

  return (
    <FilterInlineModal>
      <FlexContainer direction="column">
        <FlexContainer justifyContent="space-between" alignItems="center">
          <Label header>{t('charts.speedFilter.title')}</Label>
          <Button color="#999B95" text onClick={clearFilterValues}>{t('common.clear')}</Button>
        </FlexContainer>
        <FlexContainer>
          <SpeedInputField
            label={t('charts.speedFilter.min')}
            type="number"
            value={filterMin}
            prefix={props.units}
            onChange={e => setFilterMin(e.target.value)}
          />
          <SpeedInputField
            marginLeft="10px"
            type="number"
            label={t('charts.speedFilter.max')}
            prefix={props.units}
            value={filterMax}
            onChange={e => setFilterMax(e.target.value)}
          />
        </FlexContainer>
        <Button
          innerRef={buttonRef}
          maxWidth
          disabled={isSaveButtonDisabled}
          onClick={saveFilterValue}
        >
          {t('common.save')}
        </Button>
        {filterError && (
          <Label error>{filterError}</Label>
        )}
      </FlexContainer>
    </FilterInlineModal>
  );
};

SpeedInputField.propTypes = {
  filterMin: PropTypes.string,
  filterMax: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  units: PropTypes.string
};

SpeedInputField.defaultProps = {
  filterMin: '',
  filterMax: '',
  units: 'Hz'
};

export default styled(SpeedFilter)``;
